// src/feature-module/support/SupportActions.tsx
//
// The support write actions, as they appear on a tenant: start a read-only
// session, and waive the KYC gates.
//
// These live next to the tenant rather than on a page of their own because
// that is how they are reached in practice — you are looking at a tenant's
// problem and you need to see what they see, or unblock them. A separate
// "impersonation" screen would mean picking the tenant twice.
//
// The forms deliberately surface the backend's guardrails instead of hiding
// them: reason is required and explained, the clinical acknowledgement is a
// checkbox with the actual consequence written next to it, and the read-only
// nature of a session is stated rather than assumed.

import React, { useState } from "react";
import { Button, Modal, Form, Input, InputNumber, Checkbox, Alert, Space, Typography, message, Tag } from "antd";
import { EyeOutlined, UnlockOutlined, CopyOutlined } from "@ant-design/icons";
import {
  useGrantImpersonationMutation,
  useGrantKycExemptionMutation,
} from "../../core/redux/services/adminSupportApi";
import { getApiErrorMessageWithRef } from "../../core/utils/apiError";

const REASON_HELP = "Recorded against your account, shown to the tenant, and kept for review. Minimum 12 characters.";

// Was a local one-liner that dropped the correlation id — the single most
// useful thing on an error in THIS console, where the reader is the person who
// resolves references for a living.
const errorText = (e: unknown, fallback: string) => getApiErrorMessageWithRef(e, fallback);

/** Clinical tenants expose patient records — the extra confirmation is not decorative. */
const CLINICAL_TYPES = ["HOSPITAL", "CLINIC"];

export const SupportActions: React.FC<{
  tenantId: string;
  tenantName?: string;
  tenantType?: string;
  /** When present, actions are attributed to this case. */
  caseId?: string;
  size?: "small" | "middle";
}> = ({ tenantId, tenantName, tenantType, caseId, size = "middle" }) => {
  const [impOpen, setImpOpen] = useState(false);
  const [kycOpen, setKycOpen] = useState(false);
  const [grant, grantState] = useGrantImpersonationMutation();
  const [waive, waiveState] = useGrantKycExemptionMutation();
  const [impForm] = Form.useForm();
  const [kycForm] = Form.useForm();
  const [issued, setIssued] = useState<{ token: string; expiresAt: string; targetEmail: string } | null>(null);

  const isClinical = CLINICAL_TYPES.includes(String(tenantType ?? "").toUpperCase());

  const onGrant = async () => {
    try {
      const v = await impForm.validateFields();
      const res = await grant({ targetTenantId: tenantId, caseId, ...v }).unwrap();
      setIssued({ token: res.token, expiresAt: res.expiresAt, targetEmail: res.session.targetEmail });
      setImpOpen(false);
      impForm.resetFields();
    } catch (e: any) {
      if (e?.errorFields) return; // form validation, already shown inline
      message.error(errorText(e, "Could not start the session."));
    }
  };

  const onWaive = async (dryRun: boolean) => {
    try {
      const v = await kycForm.validateFields();
      const res = await waive({ tenantId, caseId, dryRun, ...v }).unwrap();
      (dryRun ? message.info : message.success)(res.message ?? (dryRun ? "Dry run complete." : "Gates waived."));
      if (!dryRun) { setKycOpen(false); kycForm.resetFields(); }
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(errorText(e, "Could not grant the exemption."));
    }
  };

  return (
    <>
      <Space wrap>
        <Button size={size} icon={<EyeOutlined />} onClick={() => setImpOpen(true)}>
          View as tenant
        </Button>
        <Button size={size} icon={<UnlockOutlined />} onClick={() => setKycOpen(true)}>
          Waive KYC gates
        </Button>
      </Space>

      {/* ── Impersonation ── */}
      <Modal
        title={`Start a read-only session${tenantName ? ` — ${tenantName}` : ""}`}
        open={impOpen}
        onCancel={() => setImpOpen(false)}
        onOk={onGrant}
        okText="Start session"
        confirmLoading={grantState.isLoading}
      >
        <Alert
          className="mb-3"
          type="info"
          showIcon
          message="Read-only, time-boxed, and visible to the tenant"
          description="You will see what the tenant sees. Nothing can be changed during the session, it expires within 15 minutes and cannot be extended, and the tenant is notified that support accessed their account."
        />
        {isClinical && (
          <Alert
            className="mb-3"
            type="warning"
            showIcon
            message="This is a clinical tenant"
            description="Acting as its users exposes prescriptions, lab orders and insurance claims — patient data. Only proceed with a clinical reason to do so."
          />
        )}
        <Form form={impForm} layout="vertical">
          <Form.Item
            name="reason"
            label="Reason"
            extra={REASON_HELP}
            rules={[{ required: true, min: 12, message: "Give a reason of at least 12 characters." }]}
          >
            <Input.TextArea rows={2} placeholder="e.g. Reproducing the missing invoice reported in SUP-1234" />
          </Form.Item>
          <Form.Item name="ticketRef" label="Ticket reference (optional)">
            <Input placeholder="SUP-1234" />
          </Form.Item>
          <Form.Item name="targetUserId" label="Act as a specific user (optional)" extra="Defaults to the tenant's administrator.">
            <Input placeholder="user id" />
          </Form.Item>
          {isClinical && (
            <Form.Item
              name="acknowledgeClinicalAccess"
              valuePropName="checked"
              rules={[{ validator: (_, v) => (v ? Promise.resolve() : Promise.reject(new Error("Confirm to proceed."))) }]}
            >
              <Checkbox>I have a clinical reason to access patient data in this tenant</Checkbox>
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* The token is shown once. There is no "copy later" — a new one is a new
          audited grant, which is the intended friction. */}
      <Modal
        title="Session token"
        open={!!issued}
        onCancel={() => setIssued(null)}
        footer={[<Button key="done" onClick={() => setIssued(null)}>Done</Button>]}
      >
        <Alert
          className="mb-3"
          type="success"
          showIcon
          message={`Acting as ${issued?.targetEmail}`}
          description={`Read-only. Expires ${issued ? new Date(issued.expiresAt).toLocaleTimeString() : ""}. Use it as a Bearer token; it is not shown again.`}
        />
        <Typography.Paragraph
          copyable={{ text: issued?.token ?? "", icon: <CopyOutlined /> }}
          code
          style={{ wordBreak: "break-all", fontSize: 11 }}
        >
          {issued ? `${issued.token.slice(0, 48)}…` : ""}
        </Typography.Paragraph>
      </Modal>

      {/* ── KYC exemption ── */}
      <Modal
        title={`Waive KYC gates${tenantName ? ` — ${tenantName}` : ""}`}
        open={kycOpen}
        onCancel={() => setKycOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setKycOpen(false)}>Cancel</Button>,
          <Button key="dry" onClick={() => onWaive(true)} loading={waiveState.isLoading}>Dry run</Button>,
          <Button key="apply" type="primary" danger onClick={() => onWaive(false)} loading={waiveState.isLoading}>
            Waive gates
          </Button>,
        ]}
      >
        <Alert
          className="mb-3"
          type="warning"
          showIcon
          message="This lets an unverified tenant use gated capabilities"
          description="B2B connections and payment configuration become available without APPROVED KYC. It expires on its own (72 hours maximum) and every use is logged."
        />
        <Form form={kycForm} layout="vertical" initialValues={{ expiresInHours: 24 }}>
          <Form.Item
            name="reason"
            label="Reason"
            extra={REASON_HELP}
            rules={[{ required: true, min: 12, message: "Give a reason of at least 12 characters." }]}
          >
            <Input.TextArea rows={2} placeholder="e.g. Unblocking B2B while KYC review is in progress" />
          </Form.Item>
          <Form.Item name="expiresInHours" label="Expires after (hours)" rules={[{ required: true }]}>
            <InputNumber min={1} max={72} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="ticketRef" label="Ticket reference (optional)">
            <Input placeholder="SUP-1234" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

/** Small badge for a live support session on a tenant. */
export const LiveSessionTag: React.FC<{ count?: number }> = ({ count }) =>
  count ? <Tag color="orange">{count} live support session{count > 1 ? "s" : ""}</Tag> : null;

export default SupportActions;
